/**
 * The way back into the auto-detect review queue from the incident and complaint add pages.
 *
 * When a reviewer confirms a review with "Then create … record" ticked, the drawer opens the add
 * page with a `reviewQueue` search param. Saving the record continues the queue (the next message,
 * or the inbox at its end), and Cancel returns to the message the reviewer came from. A message
 * that is both a complaint and an incident opens the complaint form first, then the incident form
 * with the new complaint linked. The param carries ids and an enum only, never a URL, so it can
 * only send the reviewer back into the auto-detect queue (October 2026 UX review, finding 5).
 */

import { Callout } from '@client/components/_ui/feedback/Callout';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { z } from 'zod';
import { requestInboxQueueResume } from './inbox-queue-scope';

export const reviewQueueReturnSchema = z
    .object({
        /** The queue message to open once the record is saved; absent at the end of the queue. */
        nextId: z.coerce.bigint().optional().catch(undefined),
        /** Set on the complaint form when the incident form should follow it. */
        followUp: z.enum(['incident']).optional().catch(undefined),
    })
    .strict();

export type ReviewQueueReturn = z.infer<typeof reviewQueueReturnSchema>;

export function useReviewQueueNavigation() {
    const orgId = useOrgId();
    const navigate = useNavigate();

    // Every way back restores the view, channel, filters, sort and page the reviewer left.
    function openInbox() {
        requestInboxQueueResume(orgId);
        void navigate({ to: '/console/$orgId/tools/auto-detect/inbox', params: { orgId } });
    }

    function openMessage(id: bigint) {
        requestInboxQueueResume(orgId);
        void navigate({
            to: '/console/$orgId/tools/auto-detect/inbox/$id/review-email-classification',
            params: { orgId, id },
        });
    }

    return {
        /** After the record is saved: the next message in the queue, or the inbox at its end. */
        continueQueue(reviewQueue: ReviewQueueReturn) {
            if (reviewQueue.nextId === undefined) openInbox();
            else openMessage(reviewQueue.nextId);
        },
        /** Cancel: back to the message the reviewer was reviewing. */
        backToMessage(messageClassificationId: bigint | undefined) {
            if (messageClassificationId === undefined) openInbox();
            else openMessage(messageClassificationId);
        },
    };
}

/** Where the reviewer goes next, stated at the top of the add page. */
export function reviewQueueNotice(record: 'incident' | 'complaint', reviewQueue: ReviewQueueReturn): string {
    const next =
        record === 'complaint' && reviewQueue.followUp === 'incident'
            ? 'the incident form opens next, then you go back to the auto-detect queue'
            : 'you go back to the auto-detect queue';
    return `Your review is saved. After you save this ${record}, ${next}. Cancel returns you to the message.`;
}

export function ReviewQueueCallout({
    record,
    reviewQueue,
}: {
    record: 'incident' | 'complaint';
    reviewQueue: ReviewQueueReturn | undefined;
}) {
    if (!reviewQueue) return null;
    return (
        <Callout variant="info" className="mb-4">
            {reviewQueueNotice(record, reviewQueue)}
        </Callout>
    );
}
